// Who a course has to credit for the data it SHOWS the reader.
//
// The stats screen used to build these lines inline, one source at a time, and it grew a gap: it
// credited vocabulary, sentences and stroke order but never the hints — which are the one feature
// with terms that make the credit mandatory. ja-en's character hints are drawn from Make Me a
// Hanzi (LGPL), KanjiVG (CC-BY-SA-3.0, by Ulrich Apel) and KANJIDIC2 (CC-BY-SA-4.0, the EDRDG);
// zh-en's from Make Me a Hanzi. CC-BY-SA makes attribution a condition, not a courtesy, and the
// app was showing that data with none (MYN-216).
//
// The rule, pulled out here so it is one decision and can be tested: a source is credited exactly
// when the reader can see what it produced. Stroke order is credited only on the courses that have
// it; hints on the same footing — the two English courses ship none (their hint row is hidden), so
// crediting them unconditionally would print a Make Me a Hanzi line on a course with no hanzi in it.
//
// Read off the data, never hardcoded: a corpus installed later carries its own credit rather than
// the app's guess, which is why each line comes from the file's own source block.

import type { Hints, Sentences, Strokes, Vocab } from './types.js';

/** One attribution line. `builtAt` is set only for the vocabulary; `derivedFrom` only when the
 *  source is itself drawn from a further work that also has to be named. */
export interface Credit {
  role: 'vocabulary' | 'sentences' | 'stroke-order' | 'hints';
  name: string;
  license: string;
  derivedFrom?: string;
  builtAt?: string;
}

export function credits(parts: {
  vocab: Vocab;
  sentences: Sentences;
  strokes?: Strokes | null;
  hints?: Hints | null;
}): Credit[] {
  const out: Credit[] = [
    {
      role: 'vocabulary',
      name: parts.vocab.source.name,
      license: parts.vocab.source.license,
      builtAt: parts.vocab.generatedAt,
    },
    { role: 'sentences', name: parts.sentences.source.name, license: parts.sentences.source.license },
  ];
  if (parts.strokes) {
    // The stroke source is an untyped map (its shape differs by corpus), so both spellings of
    // "licence" are tolerated exactly as the screen tolerated them before this moved.
    const s = parts.strokes.source;
    out.push({
      role: 'stroke-order',
      name: s.name ?? '',
      license: s.licence ?? s.license ?? '',
      derivedFrom: s.derivedFrom,
    });
  }
  if (parts.hints && Object.keys(parts.hints.hints).length > 0) {
    const s = parts.hints.source;
    out.push({ role: 'hints', name: s.name, license: s.license, derivedFrom: s.derivedFrom });
  }
  return out;
}
